import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  useToast,
  Spinner,
  Badge,
} from "@chakra-ui/react";
import { FaSearch } from "react-icons/fa";
import { InputGroup, InputLeftElement } from "@chakra-ui/react";
import { getAuditLog, AuditEntry } from "../../utils/apis/agentApi";

/**
 * The WhatsApp agent's admin audit log (verification decisions, phone edits).
 * First page of the agent-admin consolidation — read-only, so it validates the
 * whole dashboard -> Node proxy -> agent chain end to end. Matches the Chakra
 * list pattern used by pages/customer/index.tsx.
 */
const AuditLog = () => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const toast = useToast();

  const fetchLog = async () => {
    setLoading(true);
    const res = await getAuditLog(200);
    if (res.ok && res.data) {
      setEntries(res.data.entries || []);
    } else {
      toast({
        title: "Error",
        description:
          res.status === 502
            ? "The agent is unreachable right now."
            : "Failed to load the audit log.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLog();
  }, []);

  const term = search.trim().toLowerCase();
  const shown = term
    ? entries.filter((e) =>
        [e.action, e.actor, e.target]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term))
      )
    : entries;

  return (
    <Box p={5}>
      <Flex justifyContent="space-between" mb={4}>
        <InputGroup width="320px">
          <InputLeftElement
            pointerEvents="none"
            children={<FaSearch color="var(--admin-muted)" />}
          />
          <Input
            placeholder="Search action, actor or target"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        <Button
          colorScheme="purple"
          onClick={fetchLog}
          backgroundColor="#88077b"
        >
          Refresh
        </Button>
      </Flex>

      {loading ? (
        <Spinner size="xl" />
      ) : shown.length === 0 ? (
        <Text mt={6} color="admin.muted">
          No audit entries.
        </Text>
      ) : (
        <Box>
          <Table variant="striped">
            <Thead backgroundColor="#88077b">
              <Tr>
                <Th color="white">When</Th>
                <Th color="white">Action</Th>
                <Th color="white">Actor</Th>
                <Th color="white">Target</Th>
                <Th color="white">Detail</Th>
              </Tr>
            </Thead>
            <Tbody>
              {shown.map((e, index) => (
                <Tr
                  key={e.id}
                  backgroundColor={index % 2 === 0 ? "admin.surface" : "admin.selected"}
                >
                  <Td whiteSpace="nowrap">
                    {new Date(e.created_at).toLocaleString()}
                  </Td>
                  <Td>
                    <Badge colorScheme="purple">{e.action}</Badge>
                  </Td>
                  <Td>{e.actor || "-"}</Td>
                  <Td>{e.target || "-"}</Td>
                  <Td>
                    <Text
                      as="pre"
                      fontSize="xs"
                      whiteSpace="pre-wrap"
                      maxWidth="360px"
                    >
                      {e.detail ? JSON.stringify(e.detail) : "-"}
                    </Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default AuditLog;
